import { Injectable } from '@angular/core';
import { IAppSettings } from '@modelos/_appSettings/IAppSettings';

export interface EquipmentReportPrintData {
  appSettings: IAppSettings;
  equipment: any;
  maintenances: any[];
  calibrations: any[];
  usages: any[];
}

export interface EquipmentEventPrintData {
  appSettings: IAppSettings;
  eventType: 'MAINTENANCE' | 'CALIBRATION';
  event: {
    id?: string;
    event_code?: string;
    event_date?: string | Date;
    end_date?: string | Date;
    duration_hours?: number;
    description?: string;
    notes?: string;
    type?: 'PREVENTIVE' | 'CORRECTIVE'; // maintenance
    result_approved?: boolean | null;   // calibration
    new_status?: string;
    external_service?: boolean;
    certificate_number?: string;
    performed_by?: string;
    approved_by?: string;
    status?: string;
    tasks?: Array<{
      description: string;
      status: 'COMPLETED' | 'SKIPPED' | 'DEFERRED' | 'PENDING';
      notes?: string;
    }>;
    consumables?: Array<{
      name?: string;
      item_name?: string;
      quantity: number;
      unit?: string;
    }>;
    supplier?: { name?: string };
    parentEvent?: { 
      event_code?: string; 
      description?: string;
      type?: string;
      event_date?: string | Date;
      maintenance_date?: string | Date;
      calibration_date?: string | Date;
    };
  };
  equipment: {
    name?: string;
    equipment_code?: string;
    serial_number?: string;
    model?: string;
    brand?: { name?: string };
    location?: { name?: string };
    vendor?: { name?: string };
  };
}

@Injectable({ providedIn: 'root' })
export class EquipmentEventPrintService {

  private formatDate(date: string | Date | undefined | null): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  private formatDateShort(date: string | Date | undefined | null): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  }

  private translateEventStatus(status: string | undefined): string {
    const map: Record<string, string> = {
      'SUCCESSFULL': 'Completado',
      'COMPLETED':   'Completado',
      'SCHEDULLED':  'Programado',
      'SCHEDULED':   'Programado',
      'CANCELLED':   'Anulado',
      'NULLED':      'Anulado',
      'IN_PROGRESS': 'En Curso',
    };
    return map[status ?? ''] ?? status ?? '—';
  }

  private getEventTypeName(data: EquipmentEventPrintData): string {
    if (data.eventType === 'CALIBRATION') return 'CALIBRACIÓN';
    return data.event.type === 'CORRECTIVE' ? 'MANTENIMIENTO CORRECTIVO' : 'MANTENIMIENTO PREVENTIVO';
  }

  private getEventTypeColor(data: EquipmentEventPrintData): string {
    if (data.eventType === 'CALIBRATION') return '#0284c7';
    return data.event.type === 'CORRECTIVE' ? '#c2410c' : '#15803d';
  }

  private getTypeBadge(data: EquipmentEventPrintData): string {
    if (data.eventType === 'CALIBRATION') {
      return `<span class="badge" style="background:#e0f2fe;color:#0369a1;border:1px solid #7dd3fc">CALIBRACIÓN</span>`;
    }
    if (data.event.type === 'CORRECTIVE') {
      return `<span class="badge" style="background:#fff7ed;color:#c2410c;border:1px solid #fdba74">CORRECTIVO</span>`;
    }
    return `<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">PREVENTIVO</span>`;
  }

  private getStatusBadge(data: EquipmentEventPrintData): string {
    const statusLabel = this.translateEventStatus(data.event.status);
    const status = data.event.status;
    if (status === 'SUCCESSFULL' || status === 'COMPLETED') {
      return `<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">${statusLabel}</span>`;
    }
    if (status === 'SCHEDULLED' || status === 'SCHEDULED') {
      return `<span class="badge" style="background:#eff6ff;color:#1d4ed8;border:1px solid #93c5fd">${statusLabel}</span>`;
    }
    if (status === 'CANCELLED' || status === 'NULLED') {
      return `<span class="badge" style="background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5">${statusLabel}</span>`;
    }
    if (status === 'IN_PROGRESS') {
      return `<span class="badge" style="background:#fefce8;color:#92400e;border:1px solid #fcd34d">${statusLabel}</span>`;
    }
    return `<span class="badge" style="background:#f1f5f9;color:#475569;border:1px solid #cbd5e1">${statusLabel}</span>`;
  }

  private getResultBadge(data: EquipmentEventPrintData): string {
    if (data.eventType !== 'CALIBRATION') {
      const ns = data.event.new_status;
      if (ns === 'ACTIVE') return `<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">ACTIVO</span>`;
      if (ns === 'MAINTENANCE') return `<span class="badge" style="background:#fff7ed;color:#c2410c;border:1px solid #fdba74">EN MANTENIMIENTO</span>`;
      if (ns === 'OUT_OF_SERVICE') return `<span class="badge" style="background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5">FUERA DE SERVICIO</span>`;
      return '';
    }
    if (data.event.result_approved === true) {
      return `<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">APROBADA</span>`;
    }
    if (data.event.result_approved === false) {
      return `<span class="badge" style="background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5">RECHAZADA</span>`;
    }
    return '';
  }

  private getTaskStatusHtml(status: string): string {
    const map: Record<string, { label: string; color: string; bg: string; border: string }> = {
      COMPLETED: { label: '✔ Completada', color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
      SKIPPED:   { label: '✕ Omitida',   color: '#b91c1c', bg: '#fef2f2', border: '#fca5a5' },
      DEFERRED:  { label: '⟳ Diferida',  color: '#92400e', bg: '#fefce8', border: '#fcd34d' },
      PENDING:   { label: '○ Pendiente', color: '#475569', bg: '#f1f5f9', border: '#cbd5e1' },
    };
    const s = map[status] ?? map['PENDING'];
    return `<span style="font-size:8pt;font-weight:600;padding:2px 7px;border-radius:4px;background:${s.bg};color:${s.color};border:1px solid ${s.border}">${s.label}</span>`;
  }

  printEventSheet(data: EquipmentEventPrintData): void {
    const { appSettings, event, equipment } = data;
    // El nombre del cliente/laboratorio vive en general.customer
    const rawName = appSettings?.general?.customer || appSettings?.general?.lab?.name || 'Laboratorio';
    const labName = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();
    const accentColor = this.getEventTypeColor(data);
    const generatedAt = new Date().toLocaleString('es-AR');
    // El ID del evento se muestra usando el event_code generado (MNT-0001 / CAL-0001)
    const eventIdLabel = event.event_code || (event.id ? event.id.split('-')[0].toUpperCase() : 'BORRADOR');

    const calibrationRows = data.eventType === 'CALIBRATION' ? `
      <tr><td class="lbl">Nro. Certificado</td><td>${event.certificate_number || '—'}</td></tr>
    ` : '';

    const tasksRows = (event.tasks || []).length > 0 ? (event.tasks!).map((t, i) => `
      <tr style="${i % 2 === 0 ? 'background:#f8fafc' : 'background:#fff'}">
        <td style="text-align:center;color:#94a3b8;font-size:8.5pt">${i + 1}</td>
        <td>${t.description}</td>
        <td style="text-align:center">${this.getTaskStatusHtml(t.status)}</td>
        <td style="color:#64748b;font-size:8.5pt">${t.notes || ''}</td>
      </tr>
    `).join('') : `<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:12px">Sin tareas registradas</td></tr>`;

    const consumablesRows = (event.consumables || []).length > 0 ? (event.consumables!).map((c, i) => `
      <tr style="${i % 2 === 0 ? 'background:#f8fafc' : 'background:#fff'}">
        <td>${c.name || c.item_name || '—'}</td>
        <td style="text-align:center">${c.quantity ?? '—'}</td>
        <td style="text-align:center;color:#64748b">${c.unit || '—'}</td>
      </tr>
    `).join('') : '';

    const consumablesSection = (event.consumables || []).length > 0 ? `
      <div class="section-title" style="border-left-color:${accentColor}">INSUMOS UTILIZADOS</div>
      <table class="data-table">
        <thead><tr><th>Artículo</th><th>Cantidad</th><th>Unidad</th></tr></thead>
        <tbody>${consumablesRows}</tbody>
      </table>
    ` : '';

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Hoja de Trabajo - ${this.getEventTypeName(data)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: A4; margin: 0; }
    body {
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      font-size: 9pt;
      color: #1e293b;
      background: #fff;
    }
    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 0 0 12mm;
      position: relative;
    }

    /* ── HEADER ─────────────────────────────────── */
    .header {
      background: #0f172a;
      padding: 14px 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .header-left { display: flex; align-items: center; gap: 12px; }
    .header-logo {
      width: 36px; height: 36px; border-radius: 8px;
      background: ${accentColor};
      display: flex; align-items: center; justify-content: center;
      color: #fff; font-size: 16px; font-weight: 900;
    }
    .header-lab-name { color: #f1f5f9; font-size: 14pt; font-weight: 700; letter-spacing: -0.3px; }
    .header-sub { color: #94a3b8; font-size: 8pt; margin-top: 1px; }
    .header-right { text-align: right; }
    .header-doc-id { color: #e2e8f0; font-size: 9pt; font-weight: 600; font-family: monospace; letter-spacing: 0.5px; }
    .header-badges { display: flex; gap: 6px; justify-content: flex-end; margin-top: 5px; }

    .accent-bar { height: 4px; background: ${accentColor}; }

    /* ── TITLE SECTION ───────────────────────────── */
    .title-section {
      padding: 14px 20px 10px;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }
    .event-title {
      font-size: 15pt; font-weight: 800; color: #0f172a;
      text-transform: uppercase; letter-spacing: -0.3px;
      margin-bottom: 6px;
    }
    .title-badges { display: flex; gap: 6px; flex-wrap: wrap; }

    .badge {
      display: inline-block;
      font-size: 7.5pt; font-weight: 700;
      padding: 3px 9px; border-radius: 20px;
      letter-spacing: 0.3px;
    }

    /* ── BODY ────────────────────────────────────── */
    .body { padding: 14px 20px; }

    .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    .info-card {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    .info-card-header {
      background: #f1f5f9;
      padding: 7px 12px;
      font-size: 7.5pt; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.5px;
      color: #64748b;
      border-bottom: 1px solid #e2e8f0;
    }
    .info-card-body { padding: 10px 12px; }
    .info-row { display: flex; margin-bottom: 6px; gap: 6px; }
    .info-row:last-child { margin-bottom: 0; }
    .lbl { color: #64748b; font-size: 7.5pt; font-weight: 600; text-transform: uppercase; min-width: 110px; flex-shrink: 0; padding-top: 1px; }
    .val { color: #0f172a; font-size: 9pt; font-weight: 500; flex: 1; }

    .section-title {
      font-size: 8.5pt; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.5px;
      color: #334155;
      border-left: 3px solid ${accentColor};
      padding-left: 8px;
      margin: 16px 0 8px;
    }

    .description-box {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 12px;
      color: #334155;
      font-size: 9pt;
      line-height: 1.55;
      background: #fafafa;
      min-height: 52px;
    }

    /* ── TABLES ──────────────────────────────────── */
    .data-table {
      width: 100%;
      border-collapse: collapse;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
      font-size: 8.5pt;
    }
    .data-table thead tr { background: #f1f5f9; }
    .data-table th {
      padding: 7px 10px;
      text-align: left;
      font-size: 7.5pt; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.4px;
      color: #64748b;
      border-bottom: 1px solid #e2e8f0;
    }
    .data-table td {
      padding: 7px 10px;
      border-bottom: 1px solid #f1f5f9;
      color: #1e293b;
      vertical-align: middle;
    }
    .data-table tr:last-child td { border-bottom: none; }

    /* ── FOOTER ──────────────────────────────────── */
    .page-footer {
      position: fixed;
      bottom: 0;
      width: 210mm;
      background: #f1f5f9;
      border-top: 1px solid #e2e8f0;
      padding: 7px 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 7.5pt;
      color: #64748b;
    }

    @media print {
      html, body { width: 210mm; }
      .page { margin: 0; }
      .page-footer { position: fixed; bottom: 0; }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <div class="header-logo">${labName.charAt(0).toUpperCase()}</div>
      <div>
        <div class="header-lab-name">${labName}</div>
        <div class="header-sub">Sistema de Gestión de Equipos</div>
      </div>
    </div>
    <div class="header-right">
      <div class="header-doc-id">N° ${eventIdLabel}</div>
      <div class="header-badges">
        ${this.getTypeBadge(data)}
        ${this.getStatusBadge(data)}
      </div>
    </div>
  </div>
  <div class="accent-bar"></div>

  <!-- Title -->
  <div class="title-section">
    <div class="event-title">Hoja de Trabajo — ${this.getEventTypeName(data)}</div>
    <div class="title-badges">
      ${this.getResultBadge(data)}
      ${event.external_service
        ? `<span class="badge" style="background:#faf5ff;color:#7e22ce;border:1px solid #d8b4fe">SERVICIO EXTERNO</span>`
        : `<span class="badge" style="background:#eff6ff;color:#1d4ed8;border:1px solid #93c5fd">SERVICIO INTERNO</span>`}
    </div>
  </div>

  <!-- Body -->
  <div class="body">

    <!-- Two columns -->
    <div class="two-col">
      <!-- Equipment info -->
      <div class="info-card">
        <div class="info-card-header">Información del Equipo</div>
        <div class="info-card-body">
          <div class="info-row"><span class="lbl">Nombre</span><span class="val">${equipment.name || '—'}</span></div>
          <div class="info-row"><span class="lbl">Código</span><span class="val">${equipment.equipment_code || '—'}</span></div>
          <div class="info-row"><span class="lbl">Nro. de Serie</span><span class="val">${equipment.serial_number || '—'}</span></div>
          <div class="info-row"><span class="lbl">Marca / Modelo</span><span class="val">${[equipment.brand?.name, equipment.model].filter(Boolean).join(' / ') || '—'}</span></div>
          <div class="info-row"><span class="lbl">Ubicación</span><span class="val">${equipment.location?.name || '—'}</span></div>
          <div class="info-row"><span class="lbl">Proveedor</span><span class="val">${equipment.vendor?.name || '—'}</span></div>
        </div>
      </div>

      <!-- Event info -->
      <div class="info-card">
        <div class="info-card-header">Datos de la Intervención</div>
        <div class="info-card-body">
          <div class="info-row"><span class="lbl">Fecha de Inicio</span><span class="val">${this.formatDate(event.event_date)}</span></div>
          <div class="info-row"><span class="lbl">Fecha de Fin</span><span class="val">${this.formatDate(event.end_date)}</span></div>
          <div class="info-row"><span class="lbl">Duración</span><span class="val">${event.duration_hours != null ? event.duration_hours + ' hs' : '—'}</span></div>
          <div class="info-row"><span class="lbl">Empresa Técnica</span><span class="val">${event.supplier?.name || (event.external_service ? '—' : 'Equipo Interno')}</span></div>
          ${event.parentEvent ? `<div class="info-row"><span class="lbl">Evento Relacionado</span><span class="val" style="color: #0284c7; font-weight: 600;">${event.parentEvent.event_code || (event.parentEvent.type + ' (' + new Date(event.parentEvent.event_date || event.parentEvent.maintenance_date || event.parentEvent.calibration_date).toLocaleDateString('es-AR') + ')')}</span></div>` : ''}
          ${calibrationRows}
        </div>
      </div>
    </div>

    <!-- Description -->
    <div class="section-title" style="border-left-color:${accentColor}">Descripción de la Intervención</div>
    <div class="description-box">${event.description || '<span style="color:#94a3b8">Sin descripción registrada.</span>'}</div>

    ${event.notes ? `
    <div class="section-title" style="border-left-color:${accentColor}">Observaciones / Notas</div>
    <div class="description-box">${event.notes}</div>
    ` : ''}

    <!-- Tasks -->
    <div class="section-title" style="border-left-color:${accentColor}">Checklist de Tareas</div>
    <table class="data-table">
      <thead>
        <tr>
          <th style="width:30px">#</th>
          <th>Descripción de la Tarea</th>
          <th style="width:120px;text-align:center">Estado</th>
          <th style="width:160px">Notas</th>
        </tr>
      </thead>
      <tbody>${tasksRows}</tbody>
    </table>

    <!-- Consumables (if any) -->
    ${consumablesSection}

  </div>

  <!-- Footer -->
  <div class="page-footer">
    <span>Generado el ${generatedAt}</span>
    <span>${labName} — Sistema de Gestión de Equipos</span>
  </div>

</div>
<script>
  window.onload = () => { window.print(); };
</script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    }
  }

  printEquipmentReport(data: EquipmentReportPrintData) {
    const { appSettings, equipment, maintenances, calibrations, usages } = data;
    
    const labName = appSettings?.general?.lab?.name || 'Laboratorio';
    const accentColor = '#0284c7';
    const logoUrl = '';

    const generatedAt = new Date().toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

    const translateStatus = (status: any) => {
      const STATUS_MAP: Record<string, string> = {
        'ACTIVE': 'Activo / Operativo',
        'OPERATIONAL': 'Operativo',
        'OUT_OF_SERVICE': 'Fuera de Servicio',
        'MAINTENANCE': 'En Mantenimiento',
        'CALIBRATION': 'En Calibración',
        'WITHDRAWN': 'Retirado',
        'BROKEN': 'Dado de Baja'
      };
      return status ? (STATUS_MAP[status] || status) : 'Estado desconocido';
    };

    const translateEventType = (type: any) => {
      if (type === 'PREVENTIVE') return 'Preventivo';
      if (type === 'CORRECTIVE') return 'Correctivo';
      if (type === 'CALIBRATION') return 'Calibración';
      return type || '—';
    };

    const styles = `
      <style>
        body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 11px; color: #334155; margin: 0; padding: 20px; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        .page-container { max-width: 800px; margin: 0 auto; background: white; }
        .header { background: #0f172a; padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 0; }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .header-logo { width: 36px; height: 36px; border-radius: 8px; background: ${accentColor}; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 16px; font-weight: 900; }
        .header-lab-name { color: #f1f5f9; font-size: 14pt; font-weight: 700; letter-spacing: -0.3px; }
        .header-sub { color: #94a3b8; font-size: 8pt; margin-top: 1px; }
        .header-right { text-align: right; }
        .header-doc-id { color: #e2e8f0; font-size: 9pt; font-weight: 600; font-family: monospace; letter-spacing: 0.5px; }
        .header-badges { display: flex; gap: 6px; justify-content: flex-end; margin-top: 5px; }
        .accent-bar { height: 4px; background: ${accentColor}; margin-bottom: 20px; }
        .section-title { font-size: 13px; font-weight: 700; color: #1e293b; margin-top: 25px; margin-bottom: 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; display: flex; align-items: center; }
        .section-title::before { content: ""; display: inline-block; width: 4px; height: 14px; background: ${accentColor}; margin-right: 8px; border-radius: 2px; }
        
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; }
        .info-card { border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; background: #f8fafc; }
        .info-row { display: flex; margin-bottom: 6px; font-size: 11px; }
        .info-row:last-child { margin-bottom: 0; }
        .lbl { width: 120px; font-weight: 600; color: #475569; flex-shrink: 0; }
        .val { color: #0f172a; font-weight: 500; }
        
        .data-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10px; }
        .data-table th { background: #f1f5f9; color: #475569; font-weight: 600; text-align: left; padding: 6px 8px; border: 1px solid #e2e8f0; text-transform: uppercase; font-size: 9px; }
        .data-table td { padding: 6px 8px; border: 1px solid #e2e8f0; color: #334155; }
        .data-table tr:nth-child(even) td { background: #fafafa; }
        
        .page-footer { margin-top: 30px; border-top: 1px solid #e2e8f0; padding-top: 10px; display: flex; justify-content: space-between; font-size: 9px; color: #94a3b8; }
        .badge { padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 600; text-transform: uppercase; display: inline-block; }
      </style>
    `;

    const getStatusBadge = (status: string) => {
      if (status === 'ACTIVE' || status === 'OPERATIONAL') return `<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">${translateStatus(status)}</span>`;
      if (status === 'OUT_OF_SERVICE' || status === 'BROKEN') return `<span class="badge" style="background:#fef2f2;color:#b91c1c;border:1px solid #fca5a5">${translateStatus(status)}</span>`;
      return `<span class="badge" style="background:#fff7ed;color:#c2410c;border:1px solid #fdba74">${translateStatus(status)}</span>`;
    };

    let maintenancesRows = maintenances.length === 0 ? '<tr><td colspan="5" style="text-align:center;color:#94a3b8">No hay mantenimientos registrados.</td></tr>' : '';
    maintenances.forEach(m => {
      const statusBadge = m.status === 'NULLED'
        ? '<span class="badge" style="background:#f1f5f9;color:#64748b;border:1px solid #cbd5e1">ANULADO</span>'
        : (m.status === 'SCHEDULLED'
          ? '<span class="badge" style="background:#eff6ff;color:#1d4ed8;border:1px solid #93c5fd">PROGRAMADO</span>'
          : '<span class="badge" style="background:#f0fdf4;color:#15803d;border:1px solid #86efac">COMPLETADO</span>');

      maintenancesRows += `
        <tr>
          <td>${this.formatDateShort(m.event_date)}</td>
          <td>${translateEventType(m.type)}</td>
          <td>${statusBadge}</td>
          <td>${m.description || '—'}</td>
          <td>${m.external_service ? (m.supplier?.name || 'Externo') : 'Interno'}</td>
        </tr>
      `;
    });

    let calibrationsRows = calibrations.length === 0 ? '<tr><td colspan="4" style="text-align:center;color:#94a3b8">No hay calibraciones registradas.</td></tr>' : '';
    calibrations.forEach(c => {
      let resultBadge = '';
      if (c.status === 'NULLED') {
        resultBadge = '<span class="badge" style="background:#f1f5f9;color:#64748b;border:1px solid #cbd5e1">ANULADO</span>';
      } else if (c.status === 'SCHEDULLED') {
        resultBadge = '<span class="badge" style="background:#eff6ff;color:#1d4ed8;border:1px solid #93c5fd">PROGRAMADO</span>';
      } else {
        resultBadge = c.result_approved === false 
          ? '<span style="color:#b91c1c;font-weight:600">RECHAZADO</span>' 
          : '<span style="color:#15803d;font-weight:600">APROBADO</span>';
      }
      calibrationsRows += `
        <tr>
          <td>${this.formatDateShort(c.event_date)}</td>
          <td>${c.certificate_number || 'S/N'}</td>
          <td>${c.description || '—'}</td>
          <td>${resultBadge}</td>
        </tr>
      `;
    });

    let usagesRows = usages.length === 0 ? '<tr><td colspan="4" style="text-align:center;color:#94a3b8">No hay bitácora de uso registrada.</td></tr>' : '';
    usages.forEach(u => {
      usagesRows += `
        <tr>
          <td>${this.formatDate(u.start_date)}</td>
          <td>${u.end_date ? this.formatDate(u.end_date) : '<span style="color:#0284c7;font-weight:600">En curso</span>'}</td>
          <td>${u.user_id || '—'}</td>
          <td>${u.purpose || '—'}</td>
        </tr>
      `;
    });

    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Reporte - ${equipment.name}</title>
  ${styles}
</head>
<body>
<div class="page-container">

  <div class="header">
    <div class="header-left">
      <div class="header-logo">${labName.charAt(0)}</div>
      <div>
        <div class="header-lab-name">${labName}</div>
        <div class="header-sub">Sistema de Gestión de Equipos</div>
      </div>
    </div>
    <div class="header-right">
      <div class="header-doc-id">REPORTE TÉCNICO</div>
      <div class="header-badges">
        <span class="badge" style="background:#e0f2fe;color:#0369a1;border:1px solid #7dd3fc">${equipment.equipment_code || 'S/N'}</span>
      </div>
    </div>
  </div>
  <div class="accent-bar"></div>

  <div class="section-title">Información General</div>
  <div class="info-grid">
    <div class="info-card">
      <div class="info-row"><span class="lbl">Nombre</span><span class="val">${equipment.name || '—'}</span></div>
      <div class="info-row"><span class="lbl">Código</span><span class="val">${equipment.equipment_code || '—'}</span></div>
      <div class="info-row"><span class="lbl">Nro. de Serie</span><span class="val">${equipment.serial_number || '—'}</span></div>
      <div class="info-row"><span class="lbl">Marca / Modelo</span><span class="val">${[equipment.brand?.name, equipment.model].filter(Boolean).join(' / ') || '—'}</span></div>
      <div class="info-row"><span class="lbl">Ubicación</span><span class="val">${equipment.location?.name || '—'}</span></div>
    </div>
    <div class="info-card">
      <div class="info-row"><span class="lbl">Estado Actual</span><span class="val">${getStatusBadge(equipment.status)}</span></div>
      <div class="info-row"><span class="lbl">Proveedor</span><span class="val">${equipment.vendor?.name || '—'}</span></div>
      <div class="info-row"><span class="lbl">Próximo Mant.</span><span class="val">${this.formatDateShort(equipment.next_maintenance_date)}</span></div>
      <div class="info-row"><span class="lbl">Próxima Calib.</span><span class="val">${this.formatDateShort(equipment.next_calibration_date)}</span></div>
      <div class="info-row"><span class="lbl">Fecha Compra</span><span class="val">${this.formatDateShort(equipment.purchase_date)}</span></div>
    </div>
  </div>

  <div class="section-title">Historial de Mantenimientos</div>
  <table class="data-table">
    <thead>
      <tr>
        <th style="width: 14%">Fecha</th>
        <th style="width: 16%">Tipo</th>
        <th style="width: 16%">Estado</th>
        <th style="width: 36%">Descripción</th>
        <th style="width: 18%">Servicio</th>
      </tr>
    </thead>
    <tbody>${maintenancesRows}</tbody>
  </table>

  <div class="section-title">Historial de Calibraciones</div>
  <table class="data-table">
    <thead>
      <tr>
        <th style="width: 15%">Fecha</th>
        <th style="width: 20%">Certificado</th>
        <th style="width: 45%">Descripción</th>
        <th style="width: 20%">Resultado</th>
      </tr>
    </thead>
    <tbody>${calibrationsRows}</tbody>
  </table>

  <div class="section-title">Bitácora de Uso</div>
  <table class="data-table">
    <thead>
      <tr>
        <th style="width: 20%">Inicio</th>
        <th style="width: 20%">Fin</th>
        <th style="width: 30%">Responsable</th>
        <th style="width: 30%">Tarea / Propósito</th>
      </tr>
    </thead>
    <tbody>${usagesRows}</tbody>
  </table>

  <div class="page-footer">
    <span>Generado el ${generatedAt}</span>
    <span>${labName} — Sistema de Gestión de Equipos</span>
  </div>

</div>
<script>
  window.onload = () => { window.print(); };
</script>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    }
  }
}
